import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../errors.js';
import { findNewAuditId, type AdminHttpClient } from '../http.js';
import type {
  AdminGroupView,
  DesktopModelView,
  ModelRouteAdminView,
  ServiceUser,
  TenantLoginDefaultModelView,
  TenantModelAccess,
  TenantAssignment,
  ToastMessage,
} from '../types.js';
import { DataTable } from '../components/DataTable.js';
import { buildTenantCustomModelRouteInput } from './tenant-custom-model-route.js';
import { renderCapabilityBadges, renderModelSpec } from './model-capability-summary.js';
import { modelCustomerLabel } from './model-customer-label.js';

export interface ModelsContextToken {
  generation: number;
  tenantId: string;
}

export interface ModelsLifetimeToken {
  lifetimeGeneration: number;
}

export interface ModelsOperationToken extends ModelsContextToken, ModelsLifetimeToken {}

interface ModelsLoadToken extends ModelsOperationToken {
  loadGeneration: number;
}

export interface ModelsSubjectToken extends ModelsOperationToken {
  subjectGeneration: number;
  subjectId: string;
  subjectType: 'user' | 'group';
}

export interface ModelsScreenCoordinator {
  activateLifetime(): ModelsLifetimeToken;
  beginOperation(context: ModelsContextToken): ModelsOperationToken | undefined;
  beginLoad(context: ModelsContextToken): ModelsLoadToken | undefined;
  disposeLifetime(lifetime: ModelsLifetimeToken): void;
  enterTenant(tenantId: string): ModelsContextToken;
  isCurrentContext(context: ModelsContextToken): boolean;
  isCurrentLoad(load: ModelsLoadToken): boolean;
  isCurrentOperation(operation: ModelsOperationToken): boolean;
  isCurrentSubject(subject: ModelsSubjectToken): boolean;
  selectSubject(
    context: ModelsContextToken,
    subjectType: 'user' | 'group',
    subjectId: string
  ): ModelsSubjectToken | undefined;
}

export type ModelsOperationResult<T> =
  | { status: 'applied'; value: T; operation: ModelsOperationToken }
  | { status: 'error'; error: unknown; operation: ModelsOperationToken }
  | { status: 'stale' };

function isModelsSubjectOperation(
  operation: ModelsOperationToken
): operation is ModelsSubjectToken {
  const subject = operation as Partial<ModelsSubjectToken>;
  return (
    typeof subject.subjectGeneration === 'number' &&
    typeof subject.subjectId === 'string' &&
    (subject.subjectType === 'user' || subject.subjectType === 'group')
  );
}

export function isCurrentModelsOperation(
  coordinator: ModelsScreenCoordinator,
  operation: ModelsOperationToken
): boolean {
  return isModelsSubjectOperation(operation)
    ? coordinator.isCurrentSubject(operation)
    : coordinator.isCurrentOperation(operation);
}

export function isCurrentModelsResult<T>(
  coordinator: ModelsScreenCoordinator,
  result: ModelsOperationResult<T>
): result is Exclude<ModelsOperationResult<T>, { status: 'stale' }> {
  return result.status !== 'stale' && isCurrentModelsOperation(coordinator, result.operation);
}

export function createModelsScreenCoordinator(initialTenantId: string): ModelsScreenCoordinator {
  let context: ModelsContextToken = {
    generation: 0,
    tenantId: initialTenantId,
  };
  let loadGeneration = 0;
  let lifetimeGeneration = 0;
  let lifetimeActive = true;
  let subjectGeneration = 0;
  let subject: ModelsSubjectToken | undefined;

  const isCurrentContext = (candidate: ModelsContextToken) =>
    lifetimeActive &&
    candidate.generation === context.generation &&
    candidate.tenantId === context.tenantId;

  const isCurrentOperation = (candidate: ModelsOperationToken) =>
    isCurrentContext(candidate) && candidate.lifetimeGeneration === lifetimeGeneration;

  return {
    activateLifetime() {
      lifetimeGeneration += 1;
      lifetimeActive = true;
      return { lifetimeGeneration };
    },
    beginOperation(candidate) {
      if (!isCurrentContext(candidate)) return undefined;
      return { ...candidate, lifetimeGeneration };
    },
    beginLoad(candidate) {
      if (!isCurrentContext(candidate)) return undefined;
      loadGeneration += 1;
      return { ...candidate, lifetimeGeneration, loadGeneration };
    },
    disposeLifetime(lifetime) {
      if (lifetime.lifetimeGeneration !== lifetimeGeneration) return;
      lifetimeActive = false;
      loadGeneration += 1;
      subjectGeneration += 1;
      subject = undefined;
    },
    enterTenant(tenantId) {
      if (tenantId !== context.tenantId) {
        context = { generation: context.generation + 1, tenantId };
        loadGeneration += 1;
        subjectGeneration += 1;
        subject = undefined;
      }
      return context;
    },
    isCurrentContext,
    isCurrentLoad(load) {
      return isCurrentOperation(load) && load.loadGeneration === loadGeneration;
    },
    isCurrentOperation,
    isCurrentSubject(candidate) {
      return (
        subject !== undefined &&
        isCurrentOperation(candidate) &&
        candidate.subjectGeneration === subject.subjectGeneration &&
        candidate.subjectId === subject.subjectId &&
        candidate.subjectType === subject.subjectType
      );
    },
    selectSubject(candidate, subjectType, subjectId) {
      if (!isCurrentContext(candidate)) return undefined;
      if (subject && subject.subjectId === subjectId && subject.subjectType === subjectType)
        return subject;
      subjectGeneration += 1;
      subject = {
        ...candidate,
        lifetimeGeneration,
        subjectGeneration,
        subjectId,
        subjectType,
      };
      return subject;
    },
  };
}

export async function runModelsLoad<T>(
  coordinator: ModelsScreenCoordinator,
  context: ModelsContextToken,
  load: () => Promise<T>,
  apply: (value: T) => boolean,
  requiredOperation?: ModelsOperationToken
): Promise<ModelsOperationResult<T>> {
  if (requiredOperation && !isCurrentModelsOperation(coordinator, requiredOperation))
    return { status: 'stale' };
  const loadToken = coordinator.beginLoad(context);
  if (!loadToken) return { status: 'stale' };
  try {
    const value = await load();
    if (
      !coordinator.isCurrentLoad(loadToken) ||
      (requiredOperation && !isCurrentModelsOperation(coordinator, requiredOperation))
    )
      return { status: 'stale' };
    if (!apply(value)) return { status: 'stale' };
    return { operation: requiredOperation ?? loadToken, status: 'applied', value };
  } catch (error) {
    return coordinator.isCurrentLoad(loadToken) &&
      (!requiredOperation || isCurrentModelsOperation(coordinator, requiredOperation))
      ? { error, operation: requiredOperation ?? loadToken, status: 'error' }
      : { status: 'stale' };
  }
}

export async function runModelsContextOperation<T>(
  coordinator: ModelsScreenCoordinator,
  context: ModelsContextToken,
  operation: () => Promise<T>,
  requiredOperation?: ModelsOperationToken
): Promise<ModelsOperationResult<T>> {
  if (requiredOperation && !isCurrentModelsOperation(coordinator, requiredOperation))
    return { status: 'stale' };
  const operationToken = coordinator.beginOperation(context);
  if (!operationToken) return { status: 'stale' };
  try {
    const value = await operation();
    return coordinator.isCurrentOperation(operationToken) &&
      (!requiredOperation || isCurrentModelsOperation(coordinator, requiredOperation))
      ? { operation: requiredOperation ?? operationToken, status: 'applied', value }
      : { status: 'stale' };
  } catch (error) {
    return coordinator.isCurrentOperation(operationToken) &&
      (!requiredOperation || isCurrentModelsOperation(coordinator, requiredOperation))
      ? { error, operation: requiredOperation ?? operationToken, status: 'error' }
      : { status: 'stale' };
  }
}

export async function runModelsSubjectOperation<T>(
  coordinator: ModelsScreenCoordinator,
  subject: ModelsSubjectToken,
  operation: () => Promise<T>
): Promise<ModelsOperationResult<T>> {
  if (!coordinator.isCurrentSubject(subject)) return { status: 'stale' };
  try {
    const value = await operation();
    return coordinator.isCurrentSubject(subject)
      ? { operation: subject, status: 'applied', value }
      : { status: 'stale' };
  } catch (error) {
    return coordinator.isCurrentSubject(subject)
      ? { error, operation: subject, status: 'error' }
      : { status: 'stale' };
  }
}

export interface ModelSubjectDraft {
  allowedModelIds: string[];
  context: ModelsContextToken;
  subjectId: string;
  subject: ModelsSubjectToken;
  subjectType: 'user' | 'group';
}

export interface TenantCustomRouteDraft {
  backendId: string;
  context: ModelsContextToken;
  contextWindow: string;
  credentialRef: string;
  customId: string;
  customName: string;
  endpointRef: string;
  maxOutputTokens: string;
}

function contextMatches(left: ModelsContextToken, right: ModelsContextToken): boolean {
  return left.generation === right.generation && left.tenantId === right.tenantId;
}

export function createTenantCustomRouteDraft(context: ModelsContextToken): TenantCustomRouteDraft {
  return {
    backendId: '',
    context,
    contextWindow: '128000',
    credentialRef: 'secret://custom/key',
    customId: 'veluga-custom-secure',
    customName: 'Custom Secure Model',
    endpointRef: 'secret://custom/endpoint',
    maxOutputTokens: '4096',
  };
}

export function tenantCustomRouteDraftForContext(
  context: ModelsContextToken,
  draft: TenantCustomRouteDraft
): TenantCustomRouteDraft {
  return contextMatches(context, draft.context) ? draft : createTenantCustomRouteDraft(context);
}

export function createModelSubjectDraft(
  coordinator: ModelsScreenCoordinator,
  context: ModelsContextToken,
  subjectType: 'user' | 'group',
  subjectId: string,
  access: readonly TenantModelAccess[],
  catalogIds: ReadonlySet<string>
): ModelSubjectDraft | undefined {
  const subject = coordinator.selectSubject(context, subjectType, subjectId);
  if (!subject) return undefined;
  const current = access.find(
    (item) => item.subjectType === subjectType && item.subjectId === subjectId
  );
  return {
    allowedModelIds: (current?.allowedModelIds ?? []).filter((id) => catalogIds.has(id)),
    context,
    subjectId,
    subject,
    subjectType,
  };
}

export function modelAllowlistSaveInput(
  coordinator: ModelsScreenCoordinator,
  context: ModelsContextToken,
  draft: ModelSubjectDraft | undefined,
  catalogModels: readonly (string | DesktopModelView)[]
):
  | {
      defaultModelId: string;
      scopedAllowed: string[];
      subjectId: string;
      subjectType: 'user' | 'group';
    }
  | undefined {
  if (
    !draft ||
    !draft.subjectId ||
    !contextMatches(draft.context, context) ||
    !coordinator.isCurrentSubject(draft.subject)
  )
    return undefined;
  const catalogModelIds = catalogModels.map((model) =>
    typeof model === 'string' ? model : model.id
  );
  const chatModelIds = new Set(
    catalogModels
      .filter(
        // Chat-only: voice kinds (realtime, STT, TTS) are assignable but never the chat default.
        (model) => typeof model === 'string' || model.interactionKind === undefined || model.interactionKind === 'chat'
      )
      .map((model) => (typeof model === 'string' ? model : model.id))
  );
  const catalogIds = new Set(catalogModelIds);
  const scopedAllowed = draft.allowedModelIds.filter((id) => catalogIds.has(id));
  const defaultModelId = scopedAllowed.find((id) => chatModelIds.has(id));
  if (!defaultModelId) return undefined;
  return {
    defaultModelId,
    scopedAllowed,
    subjectId: draft.subjectId,
    subjectType: draft.subjectType,
  };
}

export function modelAccessCheckboxState(
  subjectType: 'user' | 'group',
  modelId: string,
  scopedAllowed: readonly string[],
  inheritedDefaultIds: ReadonlySet<string>
): { checked: boolean; inherited: boolean } {
  const inherited = subjectType === 'user' && inheritedDefaultIds.has(modelId);
  return {
    checked: inherited || scopedAllowed.includes(modelId),
    inherited,
  };
}

export function inheritedDefaultIdsForView(
  view: TenantLoginDefaultModelView | undefined,
  tenantId: string
): ReadonlySet<string> {
  return new Set(view?.tenantId === tenantId ? view.defaultMemberModelIds : []);
}

export function updateDirectModelAllowed(
  current: readonly string[],
  catalogIds: ReadonlySet<string>,
  modelId: string,
  checked: boolean
): string[] {
  const scoped = current.filter((id) => catalogIds.has(id));
  return checked ? [...new Set([...scoped, modelId])] : scoped.filter((id) => id !== modelId);
}

export function ModelAccessOption({
  model,
  subjectType,
  scopedAllowed,
  inheritedDefaultIds,
  disabled = false,
  onDirectAllowedChange,
}: {
  disabled?: boolean;
  model: DesktopModelView;
  subjectType: 'user' | 'group';
  scopedAllowed: readonly string[];
  inheritedDefaultIds: ReadonlySet<string>;
  onDirectAllowedChange: (modelId: string, checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const { checked, inherited } = modelAccessCheckboxState(
    subjectType,
    model.id,
    scopedAllowed,
    inheritedDefaultIds
  );

  return (
    <label className="check-row">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled || inherited}
        onChange={(event) => onDirectAllowedChange(model.id, event.target.checked)}
      />
      <span>
        <strong>{modelCustomerLabel(model)}</strong>
        <small>{renderModelSpec(t, model)}</small>
        {renderCapabilityBadges(t, model)}
        <small>{model.id}</small>
        {inherited ? <small>{t('models.tenantDefaultInherited')}</small> : null}
      </span>
    </label>
  );
}

export function saveDirectModelAllowlist(
  http: AdminHttpClient,
  subjectType: 'user' | 'group',
  subjectId: string,
  scopedAllowed: string[],
  defaultModelId: string
) {
  return http.updateScopedAllowlist(subjectType, subjectId, scopedAllowed, defaultModelId);
}

export function ModelsScreen({
  http,
  platformHttp,
  isPlatformAdmin = false,
  tenantId,
  onToast,
}: {
  http: AdminHttpClient;
  platformHttp?: AdminHttpClient;
  isPlatformAdmin?: boolean;
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const coordinatorRef = useRef<ModelsScreenCoordinator>();
  if (!coordinatorRef.current) coordinatorRef.current = createModelsScreenCoordinator(tenantId);
  const coordinator = coordinatorRef.current;
  const activeContext = coordinator.enterTenant(tenantId);
  const [loadedContext, setLoadedContext] = useState<ModelsContextToken>();
  const [loadedCatalog, setCatalog] = useState<DesktopModelView[]>([]);
  const [loadedUsers, setUsers] = useState<ServiceUser[]>([]);
  const [loadedGroups, setGroups] = useState<AdminGroupView[]>([]);
  const [loadedAccess, setAccess] = useState<TenantModelAccess[]>([]);
  const [loadedRoutes, setRoutes] = useState<ModelRouteAdminView[]>([]);
  const [tenantDefaultView, setTenantDefaultView] = useState<TenantLoginDefaultModelView>();
  const [subjectDraft, setSubjectDraft] = useState<ModelSubjectDraft>();
  const [customRouteDraft, setCustomRouteDraft] = useState<TenantCustomRouteDraft>(() =>
    createTenantCustomRouteDraft(activeContext)
  );
  const [customerLabels, setCustomerLabels] = useState<
    Record<string, { providerName?: string; modelName?: string }>
  >({});

  const reload = useCallback(
    (context: ModelsContextToken, requiredOperation?: ModelsOperationToken) =>
      runModelsLoad(
        coordinator,
        context,
        async () =>
          Promise.all([
            http.listCatalog(),
            http.listUsers(),
            http.listGroups(),
            http.listModelAccess(),
            http.listModelRoutes(),
            isPlatformAdmin && platformHttp
              ? platformHttp.getTenantAssignment(context.tenantId)
              : Promise.resolve<TenantAssignment | undefined>(undefined),
            http.getTenantLoginDefaultModel(),
          ]),
        ([
          nextCatalog,
          nextUsers,
          nextGroups,
          nextAccess,
          nextRoutes,
          assignment,
          nextTenantDefaultView,
        ]) => {
          if (nextTenantDefaultView.tenantId !== context.tenantId) return false;
          const assignmentModelIds = assignment ? new Set(assignment.allowedModelIds) : undefined;
          const scopedCatalog = assignmentModelIds
            ? nextCatalog.filter((model) => assignmentModelIds.has(model.id))
            : nextCatalog;
          const nextCatalogIds = new Set(scopedCatalog.map((model) => model.id));
          setCatalog(scopedCatalog);
          setCustomerLabels(
            Object.fromEntries(
              nextCatalog.flatMap((model) => {
                const value = {
                  ...(model.customerProviderName
                    ? { providerName: model.customerProviderName }
                    : {}),
                  ...(model.customerModelName ? { modelName: model.customerModelName } : {}),
                };
                return Object.keys(value).length ? [[model.id, value]] : [];
              })
            )
          );
          setUsers(nextUsers);
          setGroups(nextGroups as AdminGroupView[]);
          setAccess(nextAccess);
          setRoutes(nextRoutes);
          setTenantDefaultView(nextTenantDefaultView);
          setSubjectDraft((current) => {
            const currentForContext =
              current && contextMatches(current.context, context) ? current : undefined;
            const nextSubjectType = currentForContext?.subjectType ?? 'user';
            const nextSubjects = nextSubjectType === 'user' ? nextUsers : nextGroups;
            const nextSubjectId = nextSubjects.some(
              (subject) => subject.id === currentForContext?.subjectId
            )
              ? (currentForContext?.subjectId ?? '')
              : (nextSubjects[0]?.id ?? '');
            return createModelSubjectDraft(
              coordinator,
              context,
              nextSubjectType,
              nextSubjectId,
              nextAccess,
              nextCatalogIds
            );
          });
          setLoadedContext(context);
          return true;
        },
        requiredOperation
      ),
    [coordinator, http, isPlatformAdmin, platformHttp]
  );

  useEffect(() => {
    const lifetime = coordinator.activateLifetime();
    return () => coordinator.disposeLifetime(lifetime);
  }, [coordinator]);

  useEffect(() => {
    void reload(activeContext).then((result) => {
      if (result.status === 'error' && isCurrentModelsResult(coordinator, result))
        onToast({ tone: 'error', message: describeError(result.error, t) });
    });
  }, [activeContext, coordinator, onToast, reload, t]);

  const hasCurrentTenantData =
    loadedContext !== undefined && contextMatches(loadedContext, activeContext);
  const catalog = useMemo(
    () => (hasCurrentTenantData ? loadedCatalog : []),
    [hasCurrentTenantData, loadedCatalog]
  );
  const users = hasCurrentTenantData ? loadedUsers : [];
  const groups = hasCurrentTenantData ? loadedGroups : [];
  const access = hasCurrentTenantData ? loadedAccess : [];
  const routes = hasCurrentTenantData ? loadedRoutes : [];
  const activeSubjectDraft =
    hasCurrentTenantData && subjectDraft && contextMatches(subjectDraft.context, activeContext)
      ? subjectDraft
      : undefined;
  const subjectType = activeSubjectDraft?.subjectType ?? 'user';
  const selectedSubjectId = activeSubjectDraft?.subjectId ?? '';
  const subjects = subjectType === 'user' ? users : groups;
  const catalogIds = useMemo(() => new Set(catalog.map((model) => model.id)), [catalog]);
  const catalogById = useMemo(() => new Map(catalog.map((model) => [model.id, model])), [catalog]);
  const scopedAllowed = useMemo(
    () => (activeSubjectDraft?.allowedModelIds ?? []).filter((id) => catalogIds.has(id)),
    [activeSubjectDraft, catalogIds]
  );
  const inheritedDefaultIds = useMemo(
    () =>
      hasCurrentTenantData
        ? inheritedDefaultIdsForView(tenantDefaultView, tenantId)
        : new Set<string>(),
    [hasCurrentTenantData, tenantDefaultView, tenantId]
  );
  const saveInput = useMemo(
    () =>
      modelAllowlistSaveInput(
        coordinator,
        activeContext,
        activeSubjectDraft,
        catalog
      ),
    [activeContext, activeSubjectDraft, catalog, coordinator]
  );
  const activeCustomRouteDraft = tenantCustomRouteDraftForContext(activeContext, customRouteDraft);

  function updateCustomRouteDraft(patch: Partial<Omit<TenantCustomRouteDraft, 'context'>>) {
    setCustomRouteDraft((current) => ({
      ...tenantCustomRouteDraftForContext(activeContext, current),
      ...patch,
    }));
  }

  async function saveAllowlist(event: FormEvent) {
    event.preventDefault();
    const saveSubject = activeSubjectDraft?.subject;
    if (!saveInput || !saveSubject) return;
    try {
      const beforeResult = await runModelsSubjectOperation(coordinator, saveSubject, () =>
        http.listAudit()
      );
      if (beforeResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, beforeResult)) return;
      if (beforeResult.status === 'error') throw beforeResult.error;
      const saveResult = await runModelsSubjectOperation(coordinator, saveSubject, () =>
        saveDirectModelAllowlist(
          http,
          saveInput.subjectType,
          saveInput.subjectId,
          saveInput.scopedAllowed,
          saveInput.defaultModelId
        )
      );
      if (saveResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, saveResult)) return;
      if (saveResult.status === 'error') throw saveResult.error;
      const refresh = await reload(activeContext, saveSubject);
      if (refresh.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, refresh)) return;
      if (refresh.status === 'error') throw refresh.error;
      const afterResult = await runModelsSubjectOperation(coordinator, saveSubject, () =>
        http.listAudit()
      );
      if (afterResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, afterResult)) return;
      if (afterResult.status === 'error') throw afterResult.error;
      const auditId = findNewAuditId(beforeResult.value, afterResult.value, {
        eventType: 'admin.model_allowlist.updated',
        targetId: `${saveInput.subjectType}:${saveInput.subjectId}`,
      });
      if (!coordinator.isCurrentSubject(saveSubject)) return;
      onToast({
        tone: 'success',
        message: t('common.changedWithAudit', { auditId: auditId ?? t('common.pending') }),
      });
    } catch (error) {
      if (coordinator.isCurrentSubject(saveSubject))
        onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function addCustomRoute(event: FormEvent) {
    event.preventDefault();
    if (!hasCurrentTenantData) return;
    const operationContext = activeContext;
    const input = buildTenantCustomModelRouteInput(operationContext.tenantId, {
      customId: activeCustomRouteDraft.customId,
      customName: activeCustomRouteDraft.customName,
      backendId: activeCustomRouteDraft.backendId,
      endpointRef: activeCustomRouteDraft.endpointRef,
      credentialRef: activeCustomRouteDraft.credentialRef,
      contextWindow: activeCustomRouteDraft.contextWindow,
      maxOutputTokens: activeCustomRouteDraft.maxOutputTokens,
    });
    if (!input.ok) {
      if (coordinator.isCurrentContext(operationContext))
        onToast({ tone: 'error', message: t(input.errorKey) });
      return;
    }
    let operationToken: ModelsOperationToken | undefined;
    try {
      const beforeResult = await runModelsContextOperation(coordinator, operationContext, () =>
        http.listAudit()
      );
      if (beforeResult.status === 'stale') return;
      operationToken = beforeResult.operation;
      if (!isCurrentModelsResult(coordinator, beforeResult)) return;
      if (beforeResult.status === 'error') throw beforeResult.error;
      const createResult = await runModelsContextOperation(
        coordinator,
        operationContext,
        () => http.createModelRoute(input.route, input.catalogItem),
        operationToken
      );
      if (createResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, createResult)) return;
      if (createResult.status === 'error') throw createResult.error;
      const refresh = await reload(operationContext, operationToken);
      if (refresh.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, refresh)) return;
      if (refresh.status === 'error') throw refresh.error;
      const afterResult = await runModelsContextOperation(
        coordinator,
        operationContext,
        () => http.listAudit(),
        operationToken
      );
      if (afterResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, afterResult)) return;
      if (afterResult.status === 'error') throw afterResult.error;
      const auditId = findNewAuditId(beforeResult.value, afterResult.value, {
        eventType: 'admin.model_route.updated',
        targetId: input.route.id,
      });
      if (coordinator.isCurrentOperation(operationToken))
        onToast({
          tone: 'success',
          message: t('common.changedWithAudit', { auditId: auditId ?? t('common.pending') }),
        });
    } catch (error) {
      if (operationToken && coordinator.isCurrentOperation(operationToken))
        onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function saveCustomerLabels(event: FormEvent) {
    event.preventDefault();
    if (!hasCurrentTenantData || !platformHttp) return;
    const operationContext = activeContext;
    const labels = customerLabels;
    let operationToken: ModelsOperationToken | undefined;
    try {
      const beforeResult = await runModelsContextOperation(coordinator, operationContext, () =>
        http.listAudit()
      );
      if (beforeResult.status === 'stale') return;
      operationToken = beforeResult.operation;
      if (!isCurrentModelsResult(coordinator, beforeResult)) return;
      if (beforeResult.status === 'error') throw beforeResult.error;
      const saveResult = await runModelsContextOperation(
        coordinator,
        operationContext,
        () => platformHttp.setTenantCustomerModelLabels(operationContext.tenantId, labels),
        operationToken
      );
      if (saveResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, saveResult)) return;
      if (saveResult.status === 'error') throw saveResult.error;
      const refresh = await reload(operationContext, operationToken);
      if (refresh.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, refresh)) return;
      if (refresh.status === 'error') throw refresh.error;
      const afterResult = await runModelsContextOperation(
        coordinator,
        operationContext,
        () => http.listAudit(),
        operationToken
      );
      if (afterResult.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, afterResult)) return;
      if (afterResult.status === 'error') throw afterResult.error;
      const auditId = findNewAuditId(beforeResult.value, afterResult.value, {
        eventType: 'admin.model_display.updated',
        targetId: operationContext.tenantId,
      });
      if (coordinator.isCurrentOperation(operationToken))
        onToast({
          tone: 'success',
          message: t('common.changedWithAudit', { auditId: auditId ?? t('common.pending') }),
        });
    } catch (error) {
      if (operationToken && coordinator.isCurrentOperation(operationToken))
        onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  async function removeCustomRoute(route: ModelRouteAdminView) {
    if (!window.confirm(t('models.confirmRemoveCustomRoute', { model: route.catalogModelId })))
      return;
    const operationContext = activeContext;
    let operationToken: ModelsOperationToken | undefined;
    try {
      const removeResult = await runModelsContextOperation(coordinator, operationContext, () =>
        http.removeCustomProviderModel(route.catalogModelId)
      );
      if (removeResult.status === 'stale') return;
      operationToken = removeResult.operation;
      if (!isCurrentModelsResult(coordinator, removeResult)) return;
      if (removeResult.status === 'error') throw removeResult.error;
      const refresh = await reload(operationContext, operationToken);
      if (refresh.status === 'stale') return;
      if (!isCurrentModelsResult(coordinator, refresh)) return;
      if (refresh.status === 'error') throw refresh.error;
      if (coordinator.isCurrentOperation(operationToken))
        onToast({ tone: 'success', message: t('models.customRouteRemoved') });
    } catch (error) {
      if (operationToken && coordinator.isCurrentOperation(operationToken))
        onToast({ tone: 'error', message: describeError(error, t) });
    }
  }

  return (
    <section className="split-layout">
      <section className="panel">
        <h2>{t('models.catalog')}</h2>
        <p className="screen-help">{t('screenHelp.models')}</p>
        <div className="model-list">
          {catalog.map((model) => (
            <ModelAccessOption
              key={model.id}
              model={model}
              subjectType={subjectType}
              scopedAllowed={scopedAllowed}
              inheritedDefaultIds={inheritedDefaultIds}
              disabled={!activeSubjectDraft}
              onDirectAllowedChange={(modelId, checked) =>
                setSubjectDraft((current) => {
                  if (!current || !contextMatches(current.context, activeContext)) return current;
                  return {
                    ...current,
                    allowedModelIds: updateDirectModelAllowed(
                      current.allowedModelIds,
                      catalogIds,
                      modelId,
                      checked
                    ),
                  };
                })
              }
            />
          ))}
        </div>
      </section>
      <section className="content-stack">
        <form className="panel form-grid" onSubmit={saveAllowlist}>
          <h2>{t('models.allowlistTarget')}</h2>
          <select
            disabled={!hasCurrentTenantData}
            value={subjectType}
            onChange={(event) => {
              const next = event.target.value as 'user' | 'group';
              const nextSubjects = next === 'user' ? users : groups;
              setSubjectDraft(
                createModelSubjectDraft(
                  coordinator,
                  activeContext,
                  next,
                  nextSubjects[0]?.id ?? '',
                  access,
                  catalogIds
                )
              );
            }}
          >
            <option value="user">{t('models.user')}</option>
            <option value="group">{t('models.group')}</option>
          </select>
          <select
            disabled={!hasCurrentTenantData}
            value={selectedSubjectId}
            onChange={(event) =>
              setSubjectDraft(
                createModelSubjectDraft(
                  coordinator,
                  activeContext,
                  subjectType,
                  event.target.value,
                  access,
                  catalogIds
                )
              )
            }
          >
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {'email' in subject ? subject.email : subject.name}
              </option>
            ))}
          </select>
          {!subjects.length && <p role="status">{t(subjectType === 'group' ? 'models.noGroups' : 'models.noUsers')}</p>}
          <button disabled={!saveInput}>{t('models.saveAllowlist')}</button>
        </form>
        {isPlatformAdmin && platformHttp && (
          <form className="panel form-grid" onSubmit={saveCustomerLabels}>
            <h2>{t('models.customerLabels')}</h2>
            <p className="screen-help">{t('models.customerLabelsHelp')}</p>
            {catalog.map((model) => (
              <div key={model.id} className="form-grid" style={{ gap: 6 }}>
                <label>
                  <strong>{modelCustomerLabel(model)}</strong>
                  <small>{model.id}</small>
                </label>
                <input
                  aria-label={`${model.id} ${t('models.customerProviderName')}`}
                  placeholder={t('models.customerProviderName')}
                  value={customerLabels[model.id]?.providerName ?? ''}
                  onChange={(event) =>
                    setCustomerLabels((current) => ({
                      ...current,
                      [model.id]: { ...current[model.id], providerName: event.target.value },
                    }))
                  }
                />
                <input
                  aria-label={`${model.id} ${t('models.customerModelName')}`}
                  placeholder={t('models.customerModelName')}
                  value={customerLabels[model.id]?.modelName ?? ''}
                  onChange={(event) =>
                    setCustomerLabels((current) => ({
                      ...current,
                      [model.id]: { ...current[model.id], modelName: event.target.value },
                    }))
                  }
                />
              </div>
            ))}
            <button disabled={!hasCurrentTenantData}>{t('models.saveCustomerLabels')}</button>
          </form>
        )}
        <form className="panel form-grid" onSubmit={addCustomRoute}>
          <h2>{t('models.customProviderRoute')}</h2>
          <input
            disabled={!hasCurrentTenantData}
            value={activeCustomRouteDraft.customId}
            onChange={(event) => updateCustomRouteDraft({ customId: event.target.value })}
          />
          <input
            disabled={!hasCurrentTenantData}
            value={activeCustomRouteDraft.customName}
            onChange={(event) => updateCustomRouteDraft({ customName: event.target.value })}
          />
          <input
            disabled={!hasCurrentTenantData}
            placeholder={t('models.backendId')}
            value={activeCustomRouteDraft.backendId}
            onChange={(event) => updateCustomRouteDraft({ backendId: event.target.value })}
          />
          <input
            disabled={!hasCurrentTenantData}
            placeholder={t('models.endpointRef')}
            value={activeCustomRouteDraft.endpointRef}
            onChange={(event) => updateCustomRouteDraft({ endpointRef: event.target.value })}
          />
          <input
            disabled={!hasCurrentTenantData}
            placeholder={t('models.credentialRef')}
            value={activeCustomRouteDraft.credentialRef}
            onChange={(event) => updateCustomRouteDraft({ credentialRef: event.target.value })}
          />
          <input
            disabled={!hasCurrentTenantData}
            placeholder={t('models.contextWindow')}
            value={activeCustomRouteDraft.contextWindow}
            inputMode="numeric"
            onChange={(event) => updateCustomRouteDraft({ contextWindow: event.target.value })}
          />
          <input
            disabled={!hasCurrentTenantData}
            placeholder={t('models.maxOutputTokens')}
            value={activeCustomRouteDraft.maxOutputTokens}
            inputMode="numeric"
            onChange={(event) => updateCustomRouteDraft({ maxOutputTokens: event.target.value })}
          />
          <button
            disabled={
              !hasCurrentTenantData ||
              !activeCustomRouteDraft.customId.trim() ||
              !activeCustomRouteDraft.backendId.trim() ||
              !activeCustomRouteDraft.endpointRef.trim() ||
              !activeCustomRouteDraft.credentialRef.trim()
            }
          >
            {t('models.addCustomRoute')}
          </button>
        </form>
        <DataTable
          rows={routes}
          empty={t('models.noRoutes')}
          pageSize={20}
          getRowKey={(route) => route.id}
          columns={[
            {
              key: 'model',
              header: t('models.model'),
              render: (route) => route.catalogModelId,
              sortValue: (route) => route.catalogModelId,
            },
            {
              key: 'limits',
              header: t('models.limits'),
              render: (route) => {
                const model = catalogById.get(route.catalogModelId);
                return model ? renderModelSpec(t, model) : '—';
              },
            },
            { key: 'kind', header: t('models.kind'), render: (route) => route.routeKind },
            {
              key: 'status',
              header: t('models.status'),
              render: (route) => <span className={`badge ${route.status}`}>{route.status}</span>,
              sortValue: (route) => route.status,
            },
            {
              key: 'actions',
              header: t('common.actions'),
              render: (route) =>
                route.routeKind === 'tenant-custom' && route.managedBy === 'tenant-admin' ? (
                  <button
                    type="button"
                    disabled={route.status !== 'active'}
                    onClick={() => void removeCustomRoute(route)}
                  >
                    {t('common.delete')}
                  </button>
                ) : null,
            },
          ]}
        />
      </section>
    </section>
  );
}
